import React, { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Pagination } from '../components/Pagination';
import { LoadingScreen } from '../components/LoadingScreen';
import { PaymentLinkAPI } from '../services/api';
import { PaymentLinkData, PaymentLinkStatus, UnifiedTransaction } from '../types/payment';
import { useAuth } from '../context/AuthContext';
import { AlertService } from '../services/AlertService';
import { TransactionDetailsPanel } from '../components/TransactionDetailsPanel';

const STATUS_COLORS: Record<PaymentLinkStatus, { bg: string; color: string; border: string }> = {
  AWAITING_APPROVAL: { bg: 'rgba(168, 85, 247, 0.12)', color: '#9333ea', border: 'rgba(168, 85, 247, 0.3)' },
  REJECTED: { bg: 'rgba(239, 68, 68, 0.12)', color: '#dc2626', border: 'rgba(239, 68, 68, 0.3)' },
  PENDING: { bg: 'rgba(107, 114, 128, 0.12)', color: '#6b7280', border: 'rgba(107, 114, 128, 0.3)' },
  SENT: { bg: 'rgba(59, 130, 246, 0.12)', color: '#2563eb', border: 'rgba(59, 130, 246, 0.3)' },
  OPENED: { bg: 'rgba(245, 158, 11, 0.12)', color: '#d97706', border: 'rgba(245, 158, 11, 0.3)' },
  COMPLETED: { bg: 'rgba(16, 185, 129, 0.12)', color: '#059669', border: 'rgba(16, 185, 129, 0.3)' },
  EXPIRED: { bg: 'rgba(75, 85, 99, 0.12)', color: '#4b5563', border: 'rgba(75, 85, 99, 0.3)' },
  CANCELLED: { bg: 'rgba(239, 68, 68, 0.12)', color: '#dc2626', border: 'rgba(239, 68, 68, 0.3)' },
};

export const PaymentLinks: React.FC = () => {
  const { user } = useAuth();
  // Agents only ever see their own links, which sit in AWAITING_APPROVAL until a
  // supervisor approves and sends them — the URL isn't payable yet, so copying
  // it is meaningless. Hide the Copy button for agents.
  const isAgent = user?.role === 'agent';
  // Cancelling a payment link (at any stage) is restricted to supervisors —
  // sales team lead and above. Plain agents never see the Cancel button.
  const canCancelLinks = !user?.view_only && ['sales_team_lead', 'floor_manager', 'director', 'admin'].includes(user?.role || '');
  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState<PaymentLinkData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [threeDsFilter, setThreeDsFilter] = useState<'ALL' | 'YES' | 'NO'>('ALL');
  const [stateFilter, setStateFilter] = useState<string>('ALL');
  const [dateField, setDateField] = useState<'created' | 'paid'>('created');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  // Details panel — reuses the universal TransactionDetailsPanel. A payment
  // link maps cleanly onto UnifiedTransaction (matching field names); we fetch
  // the full record on open so approver/rejecter names are resolved.
  const [detailsLink, setDetailsLink] = useState<UnifiedTransaction | null>(null);

  // Headline counts + distinct sale states come from the server aggregate (full
  // dataset), not the fetched page. `totalCount` is the filtered total for pagination.
  const [serverStats, setServerStats] = useState<{ total: number; by_status: Record<string, number>; sale_states: string[] }>({ total: 0, by_status: {}, sale_states: [] });
  const [totalCount, setTotalCount] = useState(0);

  // Server-side: send the active filters + current page to the API. The server
  // applies status/state/3DS/date across the WHOLE dataset (not just a page).
  const loadLinks = async () => {
    try {
      setLoading(true);
      const filters = {
        status: statusFilter,
        sale_state: stateFilter,
        three_ds: threeDsFilter !== 'ALL' ? threeDsFilter : undefined,
        date_field: dateField,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      const [response, statsResp] = await Promise.all([
        PaymentLinkAPI.list((currentPage - 1) * itemsPerPage, itemsPerPage, filters),
        PaymentLinkAPI.getStats(),
      ]);
      setLinks(response.links);
      setTotalCount(response.total_count);
      setServerStats({ total: statsResp.total, by_status: statsResp.by_status || {}, sale_states: statsResp.sale_states || [] });
    } catch (error: any) {
      AlertService.error('Failed to load payment links');
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever paging or any filter changes (all filtering is server-side).
  useEffect(() => {
    loadLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, statusFilter, threeDsFilter, stateFilter, dateField, dateFrom, dateTo]);

  // State dropdown options come from the full-dataset aggregate.
  const saleStates = serverStats.sale_states;

  // Stats — sourced from the server aggregate over the full dataset (not the
  // fetched page). by_status omits zero-count statuses, so default each to 0.
  const stats = useMemo(() => {
    const bs = serverStats.by_status || {};
    return {
      total: serverStats.total,
      AWAITING_APPROVAL: bs.AWAITING_APPROVAL || 0,
      PENDING: bs.PENDING || 0,
      SENT: bs.SENT || 0,
      OPENED: bs.OPENED || 0,
      COMPLETED: bs.COMPLETED || 0,
      EXPIRED: bs.EXPIRED || 0,
      CANCELLED: bs.CANCELLED || 0,
      REJECTED: bs.REJECTED || 0,
    };
  }, [serverStats]);

  // Server returns the current page already; render it directly.
  const paginatedLinks = links;

  // Stat-card click: set the status filter and jump back to page 1 (server-side
  // paging means a stale page could otherwise land on an empty result).
  const applyStatus = (st: string) => { setStatusFilter(st); setCurrentPage(1); };

  const handleCancel = async (linkId: string) => {
    setConfirmCancelId(linkId);
  };

  const confirmCancel = async () => {
    if (!confirmCancelId) return;
    try {
      setActionLoading(confirmCancelId);
      setConfirmCancelId(null);
      await PaymentLinkAPI.cancel(confirmCancelId);
      AlertService.success('Payment link cancelled');
      loadLinks();
    } catch (error: any) {
      AlertService.error(error?.response?.data?.detail || 'Failed to cancel payment link');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResend = async (linkId: string, method: "email" | "sms" = "email") => {
    try {
      setActionLoading(linkId);
      await PaymentLinkAPI.resend(linkId, method);
      AlertService.success(method === 'sms' ? 'Payment link SMS resent' : 'Payment link email resent');
      loadLinks();
    } catch (error: any) {
      AlertService.error(error?.response?.data?.detail || 'Failed to resend payment link');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyLink = async (link: PaymentLinkData) => {
    if (!link.link_url) return;
    const markCopied = () => {
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 2000);
    };
    try {
      // navigator.clipboard is undefined outside secure contexts (and throws on
      // some browsers), so guard it and fall back to execCommand.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link.link_url);
        markCopied();
        return;
      }
      throw new Error('Clipboard API unavailable');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = link.link_url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) {
          markCopied();
        } else {
          AlertService.error('Could not copy the link automatically. Please copy it manually.');
        }
      } catch {
        AlertService.error('Could not copy the link automatically. Please copy it manually.');
      }
    }
  };

  const openDetails = async (link: PaymentLinkData) => {
    // Show immediately from the row data, then enrich with the by-id fetch
    // (which resolves approved_by_name / rejected_by_name). Falls back to the
    // row data if the fetch fails.
    setDetailsLink(link as unknown as UnifiedTransaction);
    try {
      const full = await PaymentLinkAPI.getById(link.id);
      setDetailsLink(full as unknown as UnifiedTransaction);
    } catch {
      /* keep the row data already shown */
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status: PaymentLinkStatus) => {
    const colors = STATUS_COLORS[status] || STATUS_COLORS.PENDING;
    return (
      <span
        className="status-badge"
        style={{
          background: colors.bg,
          color: colors.color,
          border: `1px solid ${colors.border}`,
        }}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <LoadingScreen isVisible={true} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="payment-links-page">
        <div className="page-header">
          <h1>Payment Links</h1>
          <button className="btn btn-outline-primary" onClick={loadLinks} disabled={loading}>
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>

        {/* Stat Cards */}
        <div className="stat-cards">
          <div className="stat-card" onClick={() => applyStatus('ALL')}>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="stat-card" onClick={() => applyStatus('AWAITING_APPROVAL')}>
            <div className="stat-value" style={{ color: '#9333ea' }}>{stats.AWAITING_APPROVAL}</div>
            <div className="stat-label">Awaiting Approval</div>
          </div>
          <div className="stat-card" onClick={() => applyStatus('PENDING')}>
            <div className="stat-value" style={{ color: '#6b7280' }}>{stats.PENDING}</div>
            <div className="stat-label">Pending</div>
          </div>
          <div className="stat-card" onClick={() => applyStatus('SENT')}>
            <div className="stat-value" style={{ color: '#2563eb' }}>{stats.SENT}</div>
            <div className="stat-label">Sent</div>
          </div>
          <div className="stat-card" onClick={() => applyStatus('OPENED')}>
            <div className="stat-value" style={{ color: '#d97706' }}>{stats.OPENED}</div>
            <div className="stat-label">Opened</div>
          </div>
          <div className="stat-card" onClick={() => applyStatus('COMPLETED')}>
            <div className="stat-value" style={{ color: '#059669' }}>{stats.COMPLETED}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-card" onClick={() => applyStatus('EXPIRED')}>
            <div className="stat-value" style={{ color: '#4b5563' }}>{stats.EXPIRED}</div>
            <div className="stat-label">Expired</div>
          </div>
          <div className="stat-card" onClick={() => applyStatus('CANCELLED')}>
            <div className="stat-value" style={{ color: '#dc2626' }}>{stats.CANCELLED}</div>
            <div className="stat-label">Cancelled</div>
          </div>
          <div className="stat-card" onClick={() => applyStatus('REJECTED')}>
            <div className="stat-value" style={{ color: '#dc2626' }}>{stats.REJECTED}</div>
            <div className="stat-label">Rejected</div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="filter-bar">
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="filter-select"
            >
              <option value="ALL">All Statuses</option>
              <option value="AWAITING_APPROVAL">Awaiting Approval</option>
              <option value="PENDING">Pending</option>
              <option value="SENT">Sent</option>
              <option value="OPENED">Opened</option>
              <option value="COMPLETED">Completed (Paid)</option>
              <option value="EXPIRED">Expired</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
          <div className="filter-group">
            <label>3DS:</label>
            <select
              value={threeDsFilter}
              onChange={(e) => { setThreeDsFilter(e.target.value as 'ALL' | 'YES' | 'NO'); setCurrentPage(1); }}
              className="filter-select"
            >
              <option value="ALL">All</option>
              <option value="YES">3DS only</option>
              <option value="NO">Non-3DS only</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Sale State:</label>
            <select
              value={stateFilter}
              onChange={(e) => { setStateFilter(e.target.value); setCurrentPage(1); }}
              className="filter-select"
            >
              <option value="ALL">All States</option>
              {saleStates.map(st => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Date:</label>
            <select
              value={dateField}
              onChange={(e) => { setDateField(e.target.value as 'created' | 'paid'); setCurrentPage(1); }}
              className="filter-select"
            >
              <option value="created">Created</option>
              <option value="paid">Paid</option>
            </select>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
              className="filter-select"
              aria-label="From date"
            />
            <span style={{ alignSelf: 'center', color: 'var(--text-secondary)' }}>to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
              className="filter-select"
              aria-label="To date"
            />
            {(dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => { setDateFrom(''); setDateTo(''); setCurrentPage(1); }}
                className="action-btn"
                title="Clear date filter"
              >
                Clear
              </button>
            )}
          </div>
          <div className="filter-info">
            Showing {links.length} of {totalCount} links
          </div>
        </div>

        {/* Table */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Amount</th>
                <th>Status</th>
                <th>3DS</th>
                <th>Sent Via</th>
                <th>Category</th>
                <th>State</th>
                <th>Created</th>
                <th>Expiry</th>
                <th>Paid</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLinks.length === 0 ? (
                <tr>
                  <td colSpan={11} className="empty-row">
                    No payment links found
                  </td>
                </tr>
              ) : (
                paginatedLinks.map(link => (
                  <tr
                    key={link.id}
                    className="clickable-row"
                    onClick={() => openDetails(link)}
                    title="View details"
                  >
                    <td className="customer-cell">
                      <div className="customer-name">
                        {link.first_name} {link.last_name}
                      </div>
                      {link.email && (
                        <div className="customer-email">{link.email}</div>
                      )}
                      {link.phone && (
                        <div className="customer-email">{link.phone}</div>
                      )}
                    </td>
                    <td className="amount-cell">
                      ${Number(link.amount).toFixed(2)}
                    </td>
                    <td>
                      {getStatusBadge(link.status as PaymentLinkStatus)}
                      {(link.attempts_total ?? 0) > 0 && (
                        <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                          <span style={{ color: '#059669' }} title="Approved attempts">✓ {link.attempts_approved ?? 0}</span>
                          <span style={{ color: '#dc2626' }} title="Failed attempts">✗ {link.attempts_failed ?? 0}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <span
                        className="delivery-badge"
                        style={
                          link.three_ds_required !== false
                            ? { background: 'rgba(16, 185, 129, 0.12)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.3)', whiteSpace: 'nowrap' }
                            : { background: 'rgba(107, 114, 128, 0.12)', color: '#6b7280', border: '1px solid rgba(107, 114, 128, 0.3)', whiteSpace: 'nowrap' }
                        }
                      >
                        {link.three_ds_required !== false ? '3DS' : 'Non-3DS'}
                      </span>
                    </td>
                    <td>
                      <span className={`delivery-badge ${link.delivery_method === 'sms' ? 'sms' : link.delivery_method === 'both' ? 'both' : 'email'}`}>
                        {link.delivery_method === 'sms' ? 'SMS' : link.delivery_method === 'both' ? 'Both' : 'Email'}
                      </span>
                    </td>
                    <td>{link.category || 'N/A'}</td>
                    <td>{link.sale_state || 'N/A'}</td>
                    <td className="date-cell">{formatDate(link.created_at)}</td>
                    <td className="date-cell">{formatDate(link.expiry_at)}</td>
                    <td className="date-cell">{link.completed_at ? formatDate(link.completed_at) : '—'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons" style={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => openDetails(link)}
                          title="View Details"
                        >
                          📋 Details
                        </button>
                        {link.link_url && !isAgent && (
                          <button
                            className="action-btn copy-btn"
                            onClick={() => handleCopyLink(link)}
                            title="Copy Link"
                          >
                            {copiedId === link.id ? 'Copied!' : 'Copy'}
                          </button>
                        )}
                        {['PENDING', 'SENT', 'OPENED'].includes(link.status) && link.email && (
                          <button
                            className="action-btn resend-btn"
                            onClick={() => handleResend(link.id, 'email')}
                            disabled={actionLoading === link.id}
                            title="Resend Email"
                          >
                            {actionLoading === link.id ? '...' : 'Email'}
                          </button>
                        )}
                        {['PENDING', 'SENT', 'OPENED'].includes(link.status) && link.phone && (
                          <button
                            className="action-btn sms-btn"
                            onClick={() => handleResend(link.id, 'sms')}
                            disabled={actionLoading === link.id}
                            title="Resend SMS"
                          >
                            {actionLoading === link.id ? '...' : 'SMS'}
                          </button>
                        )}
                        {canCancelLinks && !['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(link.status) && (
                          <button
                            className="action-btn cancel-btn"
                            onClick={() => handleCancel(link.id)}
                            disabled={actionLoading === link.id}
                            title="Cancel Link"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {Math.ceil(totalCount / itemsPerPage) > 1 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
          />
        )}
      </div>

      {/* Details Panel — reuses the universal TransactionDetailsPanel */}
      <TransactionDetailsPanel
        transaction={detailsLink}
        isOpen={!!detailsLink}
        onClose={() => setDetailsLink(null)}
      />

      {/* Cancel Confirmation Dialog */}
      {confirmCancelId && (
        <div className="dialog-overlay" onClick={() => setConfirmCancelId(null)}>
          <div className="dialog-box" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-icon warning">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 className="dialog-title">Cancel Payment Link</h3>
            <p className="dialog-message">Are you sure you want to cancel this payment link? This action cannot be undone.</p>
            <div className="dialog-actions">
              <button className="btn btn-outline-secondary" onClick={() => setConfirmCancelId(null)}>
                No, Keep It
              </button>
              <button className="btn btn-danger" onClick={confirmCancel}>
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .payment-links-page {
          padding: 0;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
        }

        .page-header h1 {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
        }

        /* Stat Cards */
        .stat-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .stat-card {
          background: var(--bg-card);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          padding: 1rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .stat-card:hover {
          border-color: var(--primary);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .stat-label {
          font-size: 0.8rem;
          color: var(--text-secondary);
          margin-top: 0.25rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        /* Filter Bar */
        .filter-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          margin-bottom: 1rem;
          padding: 0.75rem 1rem;
          background: var(--bg-card);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .filter-group label {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-secondary);
        }

        .filter-select {
          padding: 0.4rem 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 6px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.85rem;
          cursor: pointer;
        }

        .filter-info {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        /* Table */
        .table-container {
          background: var(--bg-card);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          padding: 0.75rem 1rem;
          text-align: left;
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 2px solid var(--border-primary);
          background: var(--bg-secondary);
          white-space: nowrap;
        }

        .data-table td {
          padding: 0.75rem 1rem;
          font-size: 0.875rem;
          color: var(--text-primary);
          border-bottom: 1px solid var(--border-primary);
          vertical-align: middle;
        }

        .data-table tbody tr:hover {
          background: var(--bg-hover);
        }

        .data-table tbody tr.clickable-row {
          cursor: pointer;
        }

        .empty-row {
          text-align: center;
          padding: 2rem 1rem !important;
          color: var(--text-secondary);
        }

        .customer-cell {
          min-width: 150px;
        }

        .customer-name {
          font-weight: 600;
          color: var(--text-primary);
        }

        .customer-email {
          font-size: 0.75rem;
          color: var(--text-secondary);
          margin-top: 0.125rem;
        }

        .amount-cell {
          font-weight: 600;
          font-variant-numeric: tabular-nums;
        }

        .date-cell {
          white-space: nowrap;
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        /* Status Badge */
        .status-badge {
          display: inline-block;
          padding: 0.2rem 0.6rem;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          white-space: nowrap;
        }

        /* Action Buttons */
        .action-buttons {
          display: flex;
          gap: 0.35rem;
          flex-wrap: wrap;
        }

        .action-btn {
          padding: 0.3rem 0.6rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          border: 1px solid;
          cursor: pointer;
          transition: all 0.15s ease;
          white-space: nowrap;
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .copy-btn {
          background: rgba(59, 130, 246, 0.1);
          color: #2563eb;
          border-color: rgba(59, 130, 246, 0.3);
        }

        .copy-btn:hover:not(:disabled) {
          background: rgba(59, 130, 246, 0.2);
        }

        .resend-btn {
          background: rgba(16, 185, 129, 0.1);
          color: #059669;
          border-color: rgba(16, 185, 129, 0.3);
        }

        .resend-btn:hover:not(:disabled) {
          background: rgba(16, 185, 129, 0.2);
        }

        .sms-btn {
          background: rgba(99, 102, 241, 0.1);
          color: #6366f1;
          border-color: rgba(99, 102, 241, 0.3);
        }

        .sms-btn:hover:not(:disabled) {
          background: rgba(99, 102, 241, 0.2);
        }

        .delivery-badge {
          display: inline-block;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.04em;
        }

        .delivery-badge.email {
          background: rgba(16, 185, 129, 0.12);
          color: #059669;
        }

        .delivery-badge.sms {
          background: rgba(99, 102, 241, 0.12);
          color: #6366f1;
        }

        .delivery-badge.both {
          background: rgba(139, 92, 246, 0.12);
          color: #7c3aed;
        }

        .cancel-btn {
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
          border-color: rgba(239, 68, 68, 0.3);
        }

        .cancel-btn:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.2);
        }

        .dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }

        .dialog-box {
          background: var(--bg-card);
          border-radius: 12px;
          padding: 2rem;
          max-width: 420px;
          width: 90%;
          text-align: center;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          border: 1px solid var(--border-primary);
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .dialog-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 1rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .dialog-icon.warning {
          background: rgba(245, 158, 11, 0.15);
          color: #d97706;
        }

        .dialog-icon svg {
          width: 32px;
          height: 32px;
        }

        .dialog-title {
          margin: 0 0 0.5rem;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .dialog-message {
          margin: 0 0 1.5rem;
          color: var(--text-secondary);
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .dialog-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
        }

        .dialog-actions .btn {
          min-width: 120px;
        }

        @media (max-width: 768px) {
          .stat-cards {
            grid-template-columns: repeat(3, 1fr);
          }

          .filter-bar {
            flex-direction: column;
            align-items: stretch;
          }

          .action-buttons {
            flex-direction: column;
          }
        }
      `}</style>
    </DashboardLayout>
  );
};
